import moment from 'moment-timezone';
import {I18nMessage} from '@shared/api/model/i18n';
import {DataInterval} from '../sitemanager/meters/meters.service';
import {DLSInfo, TimezoneUtil} from '@shared/util/datetime/timezone-util';

export class ChartTimestampUtils {
  public static readonly DEFAULT_LABEL_TOOLTIP_FORMAT = 'ddd DD-MMM-YYYY HH:mm';
  public static readonly TIME_ONLY_FORMAT = 'HH:mm';
  public static readonly MARKER_CHAR = '*';

  private static readonly MILLISECONDS_PER_DAY = 1000 * 60 * 60 * 24;

  public static axisXScaledLabel(viewportRange: number): string {
    const chartDaysDuration = viewportRange / ChartTimestampUtils.MILLISECONDS_PER_DAY;
    if (chartDaysDuration <= 1.1) {
      return this.TIME_ONLY_FORMAT;
    } else if (chartDaysDuration > 1.1 && chartDaysDuration <= 5) {
      return 'ddd DD MMM - HH:mm';
    } else if (chartDaysDuration > 5 && chartDaysDuration <= 31) {
      return 'ddd DD MMM YYYY';
    } else if (chartDaysDuration > 31) {
      return 'DD MMM YYYY';
    }
  }

  public static getFormatString(aggregationPeriod: DataInterval | null): string {
    let formatString: string;
    if (aggregationPeriod) {
      switch (aggregationPeriod) {
        case DataInterval.P1M:
          formatString = 'MMM YYYY';
          break;
        case DataInterval.P1W:
        case DataInterval.P1D:
          formatString = 'ddd DD-MM-YYYY';
          break;
        case DataInterval.PT1H:
        case DataInterval.PT30M:
        case DataInterval.PT15M:
        case DataInterval.PT5M:
          formatString = this.DEFAULT_LABEL_TOOLTIP_FORMAT;
          break;
        default:
          throw new Error('Cannot convert DataInterval ' + aggregationPeriod + ' to a Chart formatString');
      }
    } else {
      formatString = this.DEFAULT_LABEL_TOOLTIP_FORMAT;
    }
    return formatString;
  }

  public static getDLSInfoToDisplay(ts: moment.Moment, aggregationPeriod: DataInterval): DLSInfo {
    if (aggregationPeriod == null || DataInterval.lessThanOrEqual(aggregationPeriod, DataInterval.P1D)) {
      const dlsInfo = TimezoneUtil.getDLSInfo(ts, 'day');
      // If aggregate period is less than a day, do not show the DLS message for the time before the actual DLS cut over
      return dlsInfo && (aggregationPeriod == null || DataInterval.lessThan(aggregationPeriod, DataInterval.P1D)) && ts.isBefore(dlsInfo.dlsBound) ? null : dlsInfo;
    } else {
      return null;
    }
  }

  public static displayLeapYearInfo(ts: moment.Moment, aggregationPeriod: DataInterval): {leapYear: boolean} {
    return {leapYear: ts.isLeapYear() && ((aggregationPeriod === DataInterval.P1M && ts.month() === 1) || (aggregationPeriod === DataInterval.P1W && ts.isoWeek() === 9))};
  }

  public static getTimestampInfoToDisplay(ts: moment.Moment, aggregationPeriod: DataInterval): {dlsInfo: DLSInfo, leapYearInfo: {leapYear: boolean}} {
    return {dlsInfo: this.getDLSInfoToDisplay(ts, aggregationPeriod), leapYearInfo: this.displayLeapYearInfo(ts, aggregationPeriod)};
  }

  public static getDLSMessage(dlsInfo: DLSInfo): I18nMessage {
    if (dlsInfo) {
      return {
        messageKey: dlsInfo.shiftPeriod.amount > 0 ? 'Forward DLS units' : 'Back DLS units',
        parameters: {
          amount: Math.abs(dlsInfo.shiftPeriod.amount),
          unit: {messageKey: dlsInfo.shiftPeriod.unit === 'minute' ? 'Mins' : 'Hour'}
        }
      };
    } else {
      return null;
    }
  }

  public static getLeapYearMessage(leapYearInfo: {leapYear: boolean}): I18nMessage {
    return leapYearInfo?.leapYear ? {messageKey: 'Leap Year'} : null;
  }
}
